// Orders and the cylinder exchange (Phase 3): the Hub's queue, the courier's deliveries, the admins' overview and the
// Hub's stock. Every type and path of these APIs lives in this one file, aligned with the backend's src/modules/orders
// (orders.controller.ts, dto/order.dto.ts, order-queries.service.ts › view / OrderListItem / summary) and
// common/constants/enums.ts. The backend's answers (`Wire*`) are mapped to the screens' models here, once.
// No order ever carries the citizen's confirmation code: only the citizen's app shows it.
import { ORDER_FORM } from '@constants/limits';
import type { UploadMessages, UploadQueueOpts } from '@lib/hooks/useUploadQueue';
import { api, send, upload, type UploadOpts } from './client';
import { refFields, type CylinderRef } from './cylinderLookup';
import { query, type Cursor, type CursorPage } from './paging';

// --- enums (backend common/constants/enums.ts) ----------------------------------------------------------------------

/**
 * PLACED (Hub assigned, quota and a full cylinder reserved) → ACCEPTED → OUT_FOR_DELIVERY | READY_FOR_PICKUP →
 * COMPLETED; DELIVERY_FAILED (not at home) → OUT_FOR_DELIVERY again once, else CANCELLED.
 */
export const ORDER_STATUSES = ['PLACED', 'ACCEPTED', 'OUT_FOR_DELIVERY', 'READY_FOR_PICKUP', 'DELIVERY_FAILED', 'COMPLETED', 'CANCELLED'] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];
/** Open: still to be completed (the SLA runs). */
export const OPEN_ORDER_STATUSES: readonly OrderStatus[] = ['PLACED', 'ACCEPTED', 'OUT_FOR_DELIVERY', 'READY_FOR_PICKUP', 'DELIVERY_FAILED'];
/** The household's first order (a lent cylinder, with the deposit) or an exchange of an empty for a full one. */
export const ORDER_KINDS = ['FIRST_ISSUE', 'EXCHANGE'] as const;
export type OrderKind = (typeof ORDER_KINDS)[number];
export const FULFILMENTS = ['DELIVERY', 'PICKUP'] as const;
/** Where an order was placed: the citizens' app or the Hub counter (GET /orders?channel=). */
export const ORDER_CHANNELS = ['APP', 'COUNTER'] as const;
/** Where the order came from: the citizen app, or a walk-in at the Hub's counter. */
export type OrderChannel = 'APP' | 'COUNTER';
export type Fulfilment = (typeof FULFILMENTS)[number];
/** The empty handed back: fine, damaged (a photo, flagged for withdrawal), or none (the cylinder is flagged lost). */
export const EMPTY_CONDITIONS = ['GOOD', 'DAMAGED', 'MISSING'] as const;
export type EmptyCondition = (typeof EMPTY_CONDITIONS)[number];
export type OrderActor = 'CITIZEN' | 'STAFF' | 'SYSTEM';
/** Why the Hub cancels an order (the text beside it is free). */
export const ORDER_CANCEL_REASONS = ['CITIZEN_REQUEST', 'OUT_OF_STOCK', 'ADDRESS_UNREACHABLE', 'NOT_AT_HOME', 'DUPLICATE', 'HUB_UNAVAILABLE', 'SLA_BREACHED', 'OTHER'] as const;
export type OrderCancelReason = (typeof ORDER_CANCEL_REASONS)[number];
/**
 * What the caller may do now (the detail's `allowedActions`; the server checks again): the Hub's steps, unlocking the
 * code after too many wrong entries, taking an order back from its courier, and an admin's fine waiver.
 */
export type OrderAction = 'accept' | 'assignCourier' | 'readyForPickup' | 'completePickup' | 'cancel' | 'receiveEmpty' | 'unlockConfirmation' | 'takeBack' | 'waiveFine';
/** Why a delivery attempt failed (household_order.failure_code). */
export const DELIVERY_FAILURES = ['NOT_AT_HOME', 'CYLINDER_REFUSED', 'NO_PAYMENT', 'ADDRESS_NOT_FOUND', 'OTHER'] as const;
export type DeliveryFailure = (typeof DELIVERY_FAILURES)[number];
/** How an exchange was confirmed: the citizen's code, or without it (a household member's KTP photo and a note, reviewed). */
export type Confirmation = 'CODE' | 'NO_CODE';
/** The file kinds of an exchange (through the staff upload route, /hub-uploads): a damaged empty, the KTP of a no-code confirmation. */
export type OrderUploadKind = 'CYLINDER_DAMAGE' | 'CONFIRMATION_ID';

// --- money --------------------------------------------------------------------------------------------------------

/** What the citizen pays at the exchange, in rupiah (cash): price (+ delivery fee) (+ deposit) (+ fine). */
export interface OrderAmounts {
  price: number;
  deliveryFee: number;
  deposit: number;
  /** a fine for a damaged or missing empty, known once the condition is recorded */
  fine: number;
  total: number;
}

/** The fines of the programme for a damaged or a missing empty, rupiah. */
export interface FineRates { damaged: number; lost: number }

// --- the Hub's queue and the admins' overview -----------------------------------------------------------------------

/** A row of GET /orders (personal fields masked without pii.read). */
export interface OrderRow {
  id: number;
  /** PSN-YYMM-NNNNNN */
  number: string;
  status: OrderStatus;
  version: number;
  kind: OrderKind;
  fulfilment: Fulfilment;
  householdId: number;
  headName: string;
  villageCode: string;
  villageName: string;
  /** not in the backend's list rows yet: '' when unknown */
  districtName: string;
  regencyName: string;
  provinceCode: string;
  hubId: number;
  hubName: string;
  courierId: number | null;
  courierName: string | null;
  placedAt: string;
  /** the SLA's end (placed + SLA hours) for a delivery; null for a pickup */
  dueAt: string | null;
  /** an open order past `dueAt` */
  overdue: boolean;
  completedAt: string | null;
  cancelledAt: string | null;
  totalExpected: number;
  channel: OrderChannel;
  channelLabel: string;
  /** cylinders the household holds now */
  householdHeld: number | null;
}
export interface OrderFilter {
  status?: readonly OrderStatus[];
  hubId?: number;
  provinceCode?: string;
  regencyCode?: string;
  fulfilment?: Fulfilment;
  channel?: OrderChannel;
  overdue?: boolean;
  /** an order number */
  q?: string;
  limit?: number;
}
export type OrderPage = CursorPage<OrderRow>;
/** GET /orders/summary: per status in the caller's scope (a Hub's own for its operator), and the late ones. */
export interface OrderSummary {
  /** a Hub's, a province's or the country's */
  scope: 'hub' | 'province' | 'national';
  counts: Partial<Record<OrderStatus, number>>;
  /** open deliveries past their SLA */
  overdue: number;
  /** open deliveries due within the next 4 hours */
  dueSoon: number;
  /** every order ever placed in the summary's area (Phase 4 backends; null from an older one) */
  lifetimeTotal: number | null;
  /** completed today (WIB) */
  completedToday: number;
  total: number;
}

export interface OrderEvent { id: number; fromStatus: OrderStatus | null; toStatus: OrderStatus; actorKind: OrderActor; actorName: string | null; note: string | null; at: string }
/** The exchange recorded at COMPLETED (the movement record of the cylinder handed to the household). */
export interface OrderExchange {
  fullCylinderId: number;
  fullSerial: string;
  emptyCylinderId: number | null;
  emptySerial: string | null;
  emptyCondition: EmptyCondition;
  /** the empty was held by another household (accepted, alerted) */
  emptyNotHeldHere: boolean;
  hasPhoto: boolean;
  cashReceived: number;
  /** received − expected: positive = overcharged (alerted), negative = a shortfall (recorded) */
  cashDifference: number;
  /** the empty's way back: with the courier, received by the Hub's Reader, or received by hand ("tanpa Reader") */
  emptyReturn: 'WITH_COURIER' | 'READER' | 'MANUAL' | null;
  emptyReturnedAt: string | null;
  at: string;
}
/** GET /orders/:id. */
export interface OrderDetail extends OrderRow {
  citizenName: string;
  /** full for the assigned courier and the Hub's operator (each detail read audited); masked otherwise */
  citizenPhone: string | null;
  address: string | null;
  lat: number | null;
  lng: number | null;
  amounts: OrderAmounts;
  /** the fines that apply when the empty comes back damaged or not at all; null: the server does not say (yet) */
  fineRates: FineRates | null;
  /** the full cylinder reserved for it (shown to staff only once it is out or at the counter) */
  fullCylinder: { id: number; serialNo: string } | null;
  /** a cylinder the household holds now (the empty expected back on an exchange) */
  heldSerials: string[];
  acceptedAt: string | null;
  outForDeliveryAt: string | null;
  readyAt: string | null;
  failedAt: string | null;
  failedCount: number;
  failureCode: DeliveryFailure | null;
  failReason: string | null;
  /** an admin waived the fine (with the reason) */
  fineWaived: { reason: string; byName: string | null } | null;
  /** completed without the citizen's code (a member's KTP photo and a note): for the Hub's review */
  confirmedWithoutCode: boolean;
  cancelReasonCode: OrderCancelReason | null;
  cancelReason: string | null;
  /** too many wrong confirmation codes: the courier is locked out, the Hub resolves (pickup at the counter or cancel) */
  confirmationLocked: boolean;
  exchange: OrderExchange | null;
  events: OrderEvent[];
  allowedActions: OrderAction[];
}

/** An active courier of the Hub (GET /orders/couriers?hubId=): the assign dialog's choice. */
export interface CourierOption { id: number; name: string; openOrders: number }

/** GET /orders/stock?hubId=: the Hub's cylinders by fill, the full ones reserved by open orders, the empties out. */
export interface HubStock { hubId: number; full: number; empty: number; reserved: number; available: number; withCourier: number; dormantHeld: number | null; updatedAt: string }

export const orderPaths = {
  list: (f: OrderFilter, cursor: Cursor) =>
    `/orders${query({ status: f.status?.join(','), hubId: f.hubId, provinceCode: f.provinceCode, regencyCode: f.regencyCode, fulfilment: f.fulfilment, channel: f.channel, slaBreached: f.overdue || undefined, q: f.q, limit: f.limit, ...cursor })}`,
  /** a Hub's counts and stock (an operator's own Hub without `hubId`; a national or province account names one) */
  summary: (f: { hubId?: number; provinceCode?: string } = {}) => `/orders/summary${query(f)}`,
  detail: (id: number | string) => `/orders/${id}`,
  couriers: (hubId: number) => `/couriers${query({ hubId })}`,
  courier: (status: CourierListStatus, cursor: Cursor = {}) => `/courier/orders${query({ status: status === 'active' ? 'ACCEPTED,OUT_FOR_DELIVERY,DELIVERY_FAILED' : 'COMPLETED,CANCELLED', limit: COURIER_LIST_LIMIT, ...cursor })}`,
  courierOrder: (id: number | string) => `/courier/orders/${id}`,
};

// --- the Hub's actions (each with the version read; 409 STALE_VERSION when someone acted since) ----------------------

/** The counter exchange of a pickup (the same checks as the courier's, without GPS). */
export interface CompletePickupInput {
  version: number;
  fullCylinder: CylinderRef;
  emptyCylinder: CylinderRef | null;
  /** null for a first issue (no empty to hand back) */
  emptyCondition: EmptyCondition | null;
  photoUploadId?: string;
  cashReceived: number;
  confirmation: Confirmation;
  confirmationCode?: string;
  /** NO_CODE: the KTP photo of a household member and why there was no code */
  confirmationPhotoUploadId?: string;
  confirmationNote?: string;
  /** the device's clock when the exchange was recorded (the server's time is authoritative) */
  deviceAt: string;
  submitToken: string;
}
const act = (id: number, action: string, body: object) => send<WireOrderView>(`/orders/${id}/${action}`, body).then(toOrderDetail);
/** The exchange's common fields as the backend takes them (CompleteDeliveryDto). */
const exchangeFields = (b: Omit<CompleteDeliveryInput, 'gps'> & { gps?: Gps | null }) => ({
  submitToken: b.submitToken,
  ...refFields('empty', b.emptyCylinder),
  emptyCondition: b.emptyCondition,
  ...(b.photoUploadId ? { photoUploadId: b.photoUploadId } : {}),
  cashReceived: b.cashReceived,
  ...(b.confirmation === 'NO_CODE' ? { noCode: { photoUploadId: b.confirmationPhotoUploadId, note: b.confirmationNote } } : { confirmationCode: b.confirmationCode }),
  ...(b.gps ? { gps: b.gps } : {}),
  clientAt: b.deviceAt,
});
export const orderActions = {
  accept: (id: number, version: number) => act(id, 'accept', { version }),
  assignCourier: (id: number, version: number, courierId: number) => act(id, 'assign-courier', { version, courierId }),
  readyForPickup: (id: number, version: number) => act(id, 'ready-for-pickup', { version }),
  /** the counter exchange (CompletePickupDto: no version, the token guards it) */
  completePickup: (id: number, b: CompletePickupInput) => send<WireOrderView>(orderRequests.completePickup(id, b).path, orderRequests.completePickup(id, b).body).then(toOrderDetail),
  cancel: (id: number, version: number, reasonCode: OrderCancelReason, note: string) => act(id, 'cancel', { version, reasonCode, ...(note ? { note } : {}) }),
  /** an empty brought back, received by hand without a Reader read (POST /orders/receive-empty: by its code, audited) */
  receiveEmpty: (empty: CylinderRef, submitToken: string) => send<unknown>('/orders/receive-empty', { submitToken, ...refFields('', empty) }),
  /** after too many wrong codes, checked by the Hub: the courier may enter the code again */
  unlockConfirmation: (id: number, version: number) => act(id, 'unlock-confirmation', { version }),
  /** an order out with a courier who cannot finish it (deactivated, unreachable): back to the Hub, audited */
  takeBack: (id: number, version: number, note: string) => act(id, 'take-back', { version, ...(note ? { note } : {}) }),
  /** an admin waives the fine of a completed order, with the reason (audited; order.fine_waive) */
  waiveFine: (id: number, _version: number, reason: string) => act(id, 'waive-fine', { reason }),
  /** the instant verdict on a scanned cylinder at the counter (nothing recorded) */
  identify: (id: number, ref: CylinderRef, role: 'FULL' | 'EMPTY') => send<WireVerdict>(`/orders/${id}/identify`, { ...refFields('', ref), role }),
};

// --- the counter (walk-in) ------------------------------------------------------------------------------------------

/**
 * POST /orders/counter: a walk-in served at once — the order PLACED→COMPLETED in one transaction, the exchange, the
 * quota consumed; identity = the original KTP checked in person (required, audited) instead of the app's code.
 * An open app order of the household at this Hub answers 409 with its id (complete it through Ambil di Hub).
 */
export interface CounterOrderInput {
  householdId: number;
  fullCylinder: CylinderRef;
  emptyCylinder: CylinderRef | null;
  emptyCondition: EmptyCondition | null;
  photoUploadId?: string;
  cashReceived: number;
  identityChecked: true;
  deviceAt: string;
  submitToken: string;
}
export const counterOrder = (b: CounterOrderInput) => send<WireOrderView>('/orders/counter', orderRequests.counter(b).body).then(toOrderDetail);

// --- the courier ----------------------------------------------------------------------------------------------------

/** One page of the courier's list: a day's deliveries fit in it. */
export const COURIER_LIST_LIMIT = 50;
/** `active`: today's work (accepted, out, failed once); `done`: completed or cancelled in the last days. */
export type CourierListStatus = 'active' | 'done';
/** A delivery assigned to me (GET /courier/orders), with what the doorstep needs. */
export interface CourierOrder {
  id: number;
  number: string;
  householdId: number;
  hubId: number;
  status: OrderStatus;
  version: number;
  kind: OrderKind;
  villageName: string;
  districtName: string;
  address: string | null;
  lat: number | null;
  lng: number | null;
  /** straight-line metres from the Hub to the house pin */
  distanceM: number | null;
  dueAt: string | null;
  overdue: boolean;
  placedAt: string;
  headName: string;
  citizenPhone: string | null;
  amounts: OrderAmounts;
  fineRates: FineRates | null;
  /** the full cylinder scanned at the Hub (OUT_FOR_DELIVERY on) */
  fullSerial: string | null;
  /** an exchange expects an empty back; a first issue does not */
  expectsEmpty: boolean;
  /** the courier may not try the code again: the Hub resolves */
  confirmationLocked: boolean;
  /** wrong codes left before the lock */
  confirmationAttemptsLeft: number;
  failedCount: number;
  completedAt: string | null;
  hubName: string;
  hubLat: number | null;
  hubLng: number | null;
}

export type CourierOrderPage = CursorPage<CourierOrder>;

export interface Gps { lat: number; lng: number; accuracyM: number }
/** POST /orders/:id/complete: the exchange at the house. A resend with the same token is recorded once. */
export interface CompleteDeliveryInput {
  emptyCylinder: CylinderRef | null;
  /** null for a first issue (no empty to hand back); MISSING: the household has none to give back */
  emptyCondition: EmptyCondition | null;
  photoUploadId?: string;
  cashReceived: number;
  confirmation: Confirmation;
  confirmationCode?: string;
  confirmationPhotoUploadId?: string;
  confirmationNote?: string;
  /** null: no position (denied, no fix): the exchange is recorded and flagged */
  gps: Gps | null;
  deviceAt: string;
  submitToken: string;
}
const courierAct = (id: number, action: string, body: object) => send<WireOrderView>(`/courier/orders/${id}/${action}`, body).then(toCourierOrder);
const go = (r: { path: string; body: Record<string, unknown> }) => send<WireOrderView>(r.path, r.body).then(toCourierOrder);
/**
 * The field's sends as plain requests (a path and a JSON body), so the outbox can keep them on the device and send them
 * again after a restart — every one carries its own idempotency token.
 */
export const orderRequests = {
  outForDelivery: (id: number, full: CylinderRef, submitToken: string) => ({ path: `/courier/orders/${id}/out-for-delivery`, body: { submitToken, ...refFields('full', full) } }),
  complete: (id: number, b: CompleteDeliveryInput) => ({ path: `/courier/orders/${id}/complete`, body: exchangeFields(b) as Record<string, unknown> }),
  deliveryFailed: (id: number, failureCode: DeliveryFailure, note: string, submitToken: string) => ({ path: `/courier/orders/${id}/delivery-failed`, body: { submitToken, failureCode, ...(note ? { reason: note } : {}) } }),
  completePickup: (id: number, { fullCylinder, version: _v, ...rest }: CompletePickupInput) => ({ path: `/orders/${id}/complete-pickup`, body: { ...refFields('full', fullCylinder), ...exchangeFields(rest) } as Record<string, unknown> }),
  counter: ({ fullCylinder, emptyCylinder, deviceAt, photoUploadId, ...b }: CounterOrderInput) => ({
    path: '/orders/counter',
    body: { ...b, ...refFields('full', fullCylinder), ...refFields('empty', emptyCylinder), ...(photoUploadId ? { photoUploadId } : {}), clientAt: deviceAt } as Record<string, unknown>,
  }),
};
export const courierActions = {
  /** step 1 at the Hub: the full cylinder identified (a token per scan: a resend is recorded once) */
  outForDelivery: (id: number, full: CylinderRef, submitToken: string) => go(orderRequests.outForDelivery(id, full, submitToken)),
  complete: (id: number, b: CompleteDeliveryInput) => go(orderRequests.complete(id, b)),
  deliveryFailed: (id: number, failureCode: DeliveryFailure, note: string, submitToken: string) => go(orderRequests.deliveryFailed(id, failureCode, note, submitToken)),
  /** the citizen refused the full cylinder (dented, leaking, seal broken): another full one carried on the run instead */
  swapFull: (id: number, full: CylinderRef, note: string, submitToken: string) => courierAct(id, 'swap-full', { submitToken, ...refFields('full', full), note }),
  /** the instant verdict on a scanned cylinder at the door (nothing recorded) */
  identify: (id: number, ref: CylinderRef, role: 'FULL' | 'EMPTY') => send<WireVerdict>(`/courier/orders/${id}/identify`, { ...refFields('', ref), role }),
  detail: (id: number) => api<WireOrderView>(orderPaths.courierOrder(id)).then(toCourierOrder),
};

/** POST /hub-uploads?kind=CYLINDER_DAMAGE: the damaged empty's photo, in quarantine until the exchange takes it. */
export interface OrderUpload { uploadId: string; kind: OrderUploadKind; size: number; contentType: string; expiresAt: string }
export const uploadOrderFile = (kind: OrderUploadKind, body: Blob, opts?: UploadOpts) => upload<OrderUpload>(`/hub-uploads${query({ kind })}`, body, opts);
export function orderUploadOptions(messages: UploadMessages, formatBytes: (n: number) => string): UploadQueueOpts<OrderUploadKind> {
  return {
    send: (kind, body, opts) => uploadOrderFile(kind, body, opts),
    types: ORDER_FORM.photoTypes,
    rule: () => ({ formats: ['image'], maxBytes: ORDER_FORM.maxPhotoBytes }),
    maxPickBytes: ORDER_FORM.maxPickBytes,
    scale: ORDER_FORM.photo,
    concurrency: 1,
    autoRetries: 3,
    messages,
    formatBytes,
  };
}

// --- the backend's answers and their mapping (order-queries.service.ts) -------------------------------------------

/** GET /orders, GET /courier/orders: one row (OrderListItem). */
export interface WireOrderListItem {
  id: number;
  number: string;
  status: OrderStatus;
  statusLabel: string;
  kind: OrderKind;
  fulfilment: Fulfilment;
  placedAt: string;
  dueAt: string | null;
  slaBreached: boolean;
  hub: { id: number; name: string };
  courier: { id: number; name: string } | null;
  villageCode: string;
  villageName: string;
  citizenName: string | null;
  deliveryAddress: string | null;
  deliveryLat: number | null;
  deliveryLng: number | null;
  expectedTotal: number;
  cashReceived: number | null;
  cashOutcome: string | null;
  version: number;
  channel: OrderChannel;
  channelLabel: string;
  districtName: string | null;
  regencyName: string | null;
  householdHeld: number;
}
/** GET /orders/:id, GET /courier/orders/:id and every action's answer (order-queries.service.ts › view). */
export interface WireOrderView {
  id: number;
  number: string;
  status: OrderStatus;
  version: number;
  kind: OrderKind;
  fulfilment: Fulfilment;
  placedAt: string;
  dueAt: string | null;
  slaBreached: boolean;
  acceptedAt: string | null;
  outAt: string | null;
  readyAt: string | null;
  failedAt: string | null;
  failedCount: number;
  failureCode: DeliveryFailure | null;
  failureReason: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
  cancelReasonCode: OrderCancelReason | null;
  cancelNote: string | null;
  hub: { id: number; name: string };
  courier: { id: number; name: string } | null;
  household: { id: number; status: string; villageCode: string; villageName: string; districtName: string | null; regencyName: string | null; heldCount: number; heldSerials: string[]; headName: string | null; householdAddress: string; lat: number | null; lng: number | null };
  citizenName: string | null;
  citizenPhone: string | null;
  hubDistanceM: number | null;
  channel: OrderChannel;
  channelLabel: string;
  deliveryAddress: string | null;
  deliveryLat: number | null;
  deliveryLng: number | null;
  amounts: { price: number; deliveryFee: number; deposit: number; fine: number; expectedTotal: number; cashReceived: number | null; cashOutcome: string | null; fineWaived: boolean; fineWaiveReason: string | null; waivedBy: number | null; waivedByName: string | null; fineRates: FineRates };
  confirmation: { attempts: number; max: number; locked: boolean; withoutCode: boolean };
  fullCylinder: { id: number; serialNo: string; manufacturerSerial: string | null } | null;
  exchange: { at: string; via: 'COURIER' | 'COUNTER'; fullSerial: string; emptySerial: string | null; emptyCondition: EmptyCondition | null; emptyReceivedAt: string | null; emptyReceivedVia: 'READER' | 'MANUAL' | null; gpsDistanceM: number | null; fullMethod: string; emptyMethod: string | null; confirmation: Confirmation } | null;
  timeline: { fromStatus: OrderStatus | null; toStatus: OrderStatus; actorKind: OrderActor; actorName: string | null; note: string | null; at: string }[];
  can: Partial<Record<'accept' | 'assignCourier' | 'cancel' | 'readyForPickup' | 'completePickup' | 'takeBack' | 'unlockConfirmation' | 'waiveFine' | 'outForDelivery' | 'complete' | 'deliveryFailed' | 'swapFull', boolean>>;
}
/** GET /orders/summary?hubId=|provinceCode=: one Hub's (with its free stock), or a region's (province, national). */
export interface WireOrderSummary {
  scope: 'hub' | 'province' | 'national';
  hubId: number | null;
  provinceCode: string | null;
  byStatus: Partial<Record<OrderStatus, number>>;
  slaBreached: number;
  stock: { full: number; empty: number; reservedFull?: number; freeFull?: number; withConsumer?: number };
  emptiesExpected: number;
  /** a Hub's: cylinders its villages' households hold past DORMANT_DAYS */
  dormantHeld?: number;
  dueSoon?: number;
  completedToday?: number;
  /** every order ever placed in the area (Phase 4) */
  lifetimeTotal?: number;
}
/** POST …/identify: what the server would say about a scanned cylinder (rules.ts › Verdict), nothing recorded. */
export interface WireVerdict { verdict: 'FULL_OK' | 'FULL_REFUSED' | 'EMPTY_OWN' | 'EMPTY_OTHER_HOUSEHOLD' | 'EMPTY_REFUSED' | 'LOST' | 'UNKNOWN' | 'RETIRED'; message: string }
/** GET /couriers?hubId= */
export interface WireCourier { id: number; name: string; active: boolean; liveSessions: number; openDeliveries: number }

export const toOrderRow = (i: WireOrderListItem): OrderRow => ({
  id: i.id,
  number: i.number,
  status: i.status,
  version: i.version,
  kind: i.kind,
  fulfilment: i.fulfilment,
  householdId: 0,
  headName: i.citizenName ?? '–',
  villageCode: i.villageCode,
  villageName: i.villageName,
  districtName: i.districtName ?? '',
  regencyName: i.regencyName ?? '',
  provinceCode: i.villageCode.slice(0, 2),
  hubId: i.hub.id,
  hubName: i.hub.name,
  courierId: i.courier?.id ?? null,
  courierName: i.courier?.name ?? null,
  placedAt: i.placedAt,
  dueAt: i.dueAt,
  overdue: i.slaBreached,
  completedAt: null,
  cancelledAt: null,
  totalExpected: i.expectedTotal,
  channel: i.channel,
  channelLabel: i.channelLabel,
  householdHeld: i.householdHeld ?? null,
});
export const toOrderPage = (p: CursorPage<WireOrderListItem>): OrderPage => ({ items: p.items.map(toOrderRow), meta: p.meta });

const ACTIONS: [keyof WireOrderView['can'], OrderAction][] = [
  ['accept', 'accept'], ['assignCourier', 'assignCourier'], ['readyForPickup', 'readyForPickup'], ['completePickup', 'completePickup'], ['cancel', 'cancel'],
  ['unlockConfirmation', 'unlockConfirmation'], ['takeBack', 'takeBack'], ['waiveFine', 'waiveFine'],
];
export function toOrderDetail(v: WireOrderView): OrderDetail {
  const x = v.exchange;
  return {
    ...toOrderRow({ ...v, statusLabel: '', villageCode: v.household.villageCode, villageName: v.household.villageName, districtName: v.household.districtName, regencyName: v.household.regencyName, householdHeld: v.household.heldCount, citizenName: v.household.headName, expectedTotal: v.amounts.expectedTotal, cashReceived: v.amounts.cashReceived, cashOutcome: v.amounts.cashOutcome }),
    householdId: v.household.id,
    completedAt: v.completedAt,
    cancelledAt: v.cancelledAt,
    citizenName: v.citizenName ?? '–',
    citizenPhone: v.citizenPhone,
    address: v.deliveryAddress ?? v.household.householdAddress,
    lat: v.deliveryLat ?? v.household.lat,
    lng: v.deliveryLng ?? v.household.lng,
    amounts: { price: v.amounts.price, deliveryFee: v.amounts.deliveryFee, deposit: v.amounts.deposit, fine: v.amounts.fine, total: v.amounts.expectedTotal },
    fineRates: v.amounts.fineRates ?? null,
    fullCylinder: v.fullCylinder ? { id: v.fullCylinder.id, serialNo: v.fullCylinder.serialNo } : null,
    heldSerials: v.household.heldSerials ?? [],
    acceptedAt: v.acceptedAt,
    outForDeliveryAt: v.outAt,
    readyAt: v.readyAt,
    failedAt: v.failedAt,
    failedCount: v.failedCount,
    failureCode: v.failureCode,
    failReason: v.failureReason,
    fineWaived: v.amounts.fineWaived ? { reason: v.amounts.fineWaiveReason ?? '', byName: v.amounts.waivedByName } : null,
    confirmedWithoutCode: v.confirmation.withoutCode,
    cancelReasonCode: v.cancelReasonCode,
    cancelReason: v.cancelNote,
    confirmationLocked: v.confirmation.locked,
    exchange: x
      ? {
          fullCylinderId: v.fullCylinder?.id ?? 0,
          fullSerial: x.fullSerial,
          emptyCylinderId: null,
          emptySerial: x.emptySerial,
          emptyCondition: x.emptyCondition ?? 'GOOD',
          emptyNotHeldHere: false,
          hasPhoto: false,
          cashReceived: v.amounts.cashReceived ?? 0,
          cashDifference: (v.amounts.cashReceived ?? 0) - v.amounts.expectedTotal,
          emptyReturn: x.emptyReceivedVia ?? (x.emptySerial ? 'WITH_COURIER' : null),
          emptyReturnedAt: x.emptyReceivedAt,
          at: x.at,
        }
      : null,
    events: v.timeline.map((e, i) => ({ id: i, ...e })),
    allowedActions: ACTIONS.filter(([k]) => v.can[k]).map(([, a]) => a),
  };
}
export function toCourierOrder(v: WireOrderView): CourierOrder {
  return {
    id: v.id,
    number: v.number,
    householdId: v.household.id,
    hubId: v.hub.id,
    status: v.status,
    version: v.version,
    kind: v.kind,
    villageName: v.household.villageName,
    districtName: v.household.districtName ?? '',
    address: v.deliveryAddress ?? v.household.householdAddress,
    lat: v.deliveryLat ?? v.household.lat,
    lng: v.deliveryLng ?? v.household.lng,
    distanceM: v.hubDistanceM,
    dueAt: v.dueAt,
    overdue: v.slaBreached,
    placedAt: v.placedAt,
    headName: v.citizenName ?? v.household.headName ?? '–',
    citizenPhone: v.citizenPhone,
    amounts: { price: v.amounts.price, deliveryFee: v.amounts.deliveryFee, deposit: v.amounts.deposit, fine: v.amounts.fine, total: v.amounts.expectedTotal },
    fineRates: v.amounts.fineRates ?? null,
    fullSerial: v.fullCylinder?.serialNo ?? null,
    expectsEmpty: v.kind === 'EXCHANGE',
    confirmationLocked: v.confirmation.locked,
    confirmationAttemptsLeft: Math.max(0, v.confirmation.max - v.confirmation.attempts),
    failedCount: v.failedCount,
    completedAt: v.completedAt,
    hubName: v.hub.name,
    hubLat: null,
    hubLng: null,
  };
}
/** A courier's list row (fewer fields than the order): enough for the card. */
export const toCourierRow = (i: WireOrderListItem): CourierOrder => ({
  ...toCourierOrder({
    ...i,
    acceptedAt: null, outAt: null, readyAt: null, failedAt: null, failedCount: 0, failureCode: null, failureReason: null, completedAt: null, cancelledAt: null, cancelReasonCode: null, cancelNote: null,
    household: { id: 0, status: '', villageCode: i.villageCode, villageName: i.villageName, districtName: i.districtName, regencyName: i.regencyName, heldCount: i.householdHeld, heldSerials: [], headName: i.citizenName, householdAddress: i.deliveryAddress ?? '', lat: i.deliveryLat, lng: i.deliveryLng },
    citizenPhone: null,
    hubDistanceM: null,
    amounts: { price: 0, deliveryFee: 0, deposit: 0, fine: 0, expectedTotal: i.expectedTotal, cashReceived: i.cashReceived, cashOutcome: i.cashOutcome, fineWaived: false, fineWaiveReason: null, waivedBy: null, waivedByName: null, fineRates: { damaged: 0, lost: 0 } },
    confirmation: { attempts: 0, max: 0, locked: false, withoutCode: false },
    fullCylinder: null, exchange: null, timeline: [], can: {},
  }),
  hubId: i.hub.id,
});
export const toCourierPage = (p: CursorPage<WireOrderListItem>): CourierOrderPage => ({ items: p.items.map(toCourierRow), meta: p.meta });
export function toSummary(w: WireOrderSummary | null): OrderSummary {
  const counts = w?.byStatus ?? {};
  return { scope: w?.scope ?? 'national', counts, overdue: w?.slaBreached ?? 0, dueSoon: w?.dueSoon ?? 0, completedToday: w?.completedToday ?? 0, lifetimeTotal: w?.lifetimeTotal ?? null, total: Object.values(counts).reduce((a, b) => a + (b ?? 0), 0) };
}
/** A Hub's stock (free and reserved full ones are a Hub's only). */
export const toStock = (w: WireOrderSummary | null): HubStock | null =>
  w && w.hubId !== null ? { hubId: w.hubId, full: w.stock.full, empty: w.stock.empty, reserved: w.stock.reservedFull ?? 0, available: w.stock.freeFull ?? 0, withCourier: w.emptiesExpected, dormantHeld: w.dormantHeld ?? null, updatedAt: new Date().toISOString() } : null;
export const toCouriers = (w: WireCourier[]): CourierOption[] => w.filter((c) => c.active).map((c) => ({ id: c.id, name: c.name, openOrders: c.openDeliveries }));

// --- a Hub's couriers (GET /couriers, POST /couriers/:id/revoke-sessions) --------------------------------------------
export const courierPaths = {
  list: (hubId?: number) => `/couriers${query({ hubId })}`,
  /** GET /couriers/recap?hubId=&from=&to=: per courier over WIB days (at most 92) */
  recap: (hubId: number, from: string, to: string) => `/couriers/recap${query({ hubId, from, to })}`,
  /** GET /cash-handovers?hubId=&date=: the day's cash per courier, expected vs handed over */
  cash: (hubId: number, date: string) => `/cash-handovers${query({ hubId, date })}`,
};

/** One courier's numbers over a period (and the Hub's own counter apart). Names masked without pii.read. */
export interface CourierRecapRow { courierId: number; name: string; active: boolean; delivered: number; failed: number; late: number; mismatches: number; gpsFar: number; noCode: number; cashExpected: number; cashReceived: number }
export interface CourierRecap { hub: { id: number; name: string }; from: string; to: string; couriers: CourierRecapRow[]; counter: { exchanges: number; cashExpected: number; cashReceived: number } }
/** A courier's cash of one WIB day: expected (the cash taken at its exchanges), handed over (null: not recorded yet). */
export interface CashHandoverRow { courierId: number; name: string; expected: number; handedOver: number | null; difference: number; recordedAt: string | null; by: { id: number; name: string } | null }
export interface CashHandovers { hub: { id: number; name: string }; date: string; couriers: CashHandoverRow[]; counter: { exchanges: number; cashReceived: number } }
/** POST: the day's handover (one per courier and day); PUT: its correction, with the reason (audited). */
export const cashActions = {
  record: (courierId: number, body: { date: string; amount: number; note?: string }) => send(`/couriers/${courierId}/cash-handovers`, body),
  correct: (courierId: number, date: string, body: { amount: number; reason: string }) => send(`/couriers/${courierId}/cash-handovers/${date}`, body, 'PUT'),
};
/** Every session of a courier of this Hub ends now (a lost phone, an account used on two devices): case 41. */
export const revokeCourierSessions = (courierId: number) => send<{ courierId: number; revoked: number }>(`/couriers/${courierId}/revoke-sessions`, {});
